/**
 * The logging correlation context — the seam that carries `sessionId`, and
 * the app's bound Logger factory, down to any `useLogger()` call in the tree
 * (see ./use-logger.ts).
 *
 * `leadId` IS part of it, unlike user identity: every conversion query joins
 * an ad click to what the reader did next on that one key, so it has to be on
 * every event rather than supplied per call.
 *
 * User identity is not. Authenticated consumers layer their auth provider's
 * opaque subject in per call with `useLogger({ userId })`; see
 * ./logger-provider.tsx for why the composition root stays above auth.
 */
import { createContext, use } from "react";
import type { ClientLogging } from "../client/client-logging";

export interface LoggerContextValue {
  sessionId: string;
  /**
   * The app's bound construction path (`ClientLogging.createLogger`), so
   * `useLogger` builds Loggers with the app's service, backend and visit id
   * without importing any app's configuration.
   */
  createLogger: ClientLogging["createLogger"];
  /**
   * The marketing lead this browser arrived as, resolved once per landing by
   * LoggerProvider's `resolveLeadId`. `undefined` for a visit that carried no campaign
   * parameters and had no lead stored from an earlier one.
   */
  leadId?: string;
}

export const LoggerContext = createContext<LoggerContextValue | null>(null);

/** Read the logging context. Throws outside a `LoggerProvider`. */
export function useLoggerContext(): LoggerContextValue {
  const ctx = use(LoggerContext);
  if (!ctx) {
    throw new Error("useLoggerContext must be used within a LoggerProvider");
  }
  return ctx;
}
